/**
 * rev 3.3 §6.5 (F3) — bounded retry for the `wa-message-listener-event` POST on
 * HTTP 503 ONLY.
 *
 * The backend's agent ingress awaits its insert + enqueue and answers 503 when
 * that fails, BEFORE any side effect (no webhook fan-out, no enqueue), so a
 * re-delivery is safe and is how the engine gets the event through a transient
 * backend/DB hiccup. Every other outcome (2xx, 4xx, 500, network error) is
 * returned unchanged on the first attempt — exactly as before.
 *
 * Kept free of the backend-api module graph so it is unit-testable in isolation.
 */
export interface RetryOn503Options {
  /** Retries after the first attempt (default 3 → at most 4 requests). */
  retries?: number;
  /** Backoff before retry n (0-based) = baseDelayMs * 2^n (default 500ms → 0.5s, 1s, 2s). */
  baseDelayMs?: number;
  /** Injectable for tests. */
  sleep?: (ms: number) => Promise<void>;
}

export function isHttp503(result: unknown): boolean {
  const error = (result as { error?: unknown } | null | undefined)?.error as { status?: unknown; originalStatus?: unknown } | undefined;
  if (!error) return false;
  return error.status === 503 || error.originalStatus === 503;
}

export async function runWithRetryOn503<R>(
  run: () => PromiseLike<R> | R,
  options: RetryOn503Options = {},
): Promise<R> {
  const retries = options.retries ?? 3;
  const baseDelayMs = options.baseDelayMs ?? 500;
  const sleep = options.sleep ?? ((ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms)));
  let result = await run();
  for (let attempt = 0; attempt < retries && isHttp503(result); attempt++) {
    const delay = baseDelayMs * 2 ** attempt;
    console.warn(`[wa-message-listener-event] 503 from backend — retry ${attempt + 1}/${retries} in ${delay}ms`);
    await sleep(delay);
    result = await run();
  }
  return result;
}
